import React, { useState, useEffect } from 'react';
import { 
  Table, Card, Input, Button, Space, Tag, message, Typography, Row, Col, Divider 
} from 'antd';
import { SearchOutlined, LockOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;

const PermissionManagement = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const { authToken } = useAuth();

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/roles/permissions', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      message.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const filteredPermissions = permissions.filter(permission => 
    permission.name.toLowerCase().includes(searchText.toLowerCase()) ||
    permission.description?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Permission',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.isSystem && (
            <div>
              <Tag color="orange" icon={<LockOutlined />}>
                System
              </Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || <span style={{ color: '#999' }}>No description</span>,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (text) => (
        <Tag color={getCategoryColor(text)}>
          {text || 'Uncategorized'}
        </Tag>
      ),
      filters: [
        { text: 'User Management', value: 'user' },
        { text: 'Role Management', value: 'role' },
        { text: 'Contract Management', value: 'contract' },
        { text: 'Policy', value: 'policy' },
        { text: 'License', value: 'license' },
        { text: 'Civil ID', value: 'civilid' },
        { text: 'Department Management', value: 'department' },
        { text: 'System', value: 'system' },
      ],
      onFilter: (value, record) => record.category === value,
    },
  ];

  const getCategoryColor = (category) => {
    const colors = {
      user: 'blue',
      role: 'purple',
      contract: 'green',
      policy: 'volcano',
      license: 'geekblue',
      civilid: 'lime',
      department: 'cyan',
      system: 'orange',
    };
    return colors[category] || 'default';
  };

  const permissionCategories = [
    {
      title: 'User Management',
      key: 'user',
      permissions: [
        'users:read',
        'users:create',
        'users:update',
        'users:delete',
      ],
    },
    {
      title: 'Role Management',
      key: 'role',
      permissions: [
        'roles:read',
        'roles:create',
        'roles:update',
        'roles:delete',
      ],
    },
    {
      title: 'Contract Management',
      key: 'contract',
      permissions: [
        'contracts:read',
        'contracts:create',
        'contracts:update',
        'contracts:delete',
        'contracts:export',
      ],
    },
    {
      title: 'Policy',
      key: 'policy',
      permissions: [
        'policies:read',
        'policies:create',
        'policies:update',
        'policies:delete',
      ],
    },
    {
      title: 'License',
      key: 'license',
      permissions: [
        'licenses:read',
        'licenses:create',
        'licenses:update',
        'licenses:delete',
      ],
    },
    {
      title: 'Civil ID',
      key: 'civilid',
      permissions: [
        'civilid:read',
        'civilid:create',
        'civilid:update',
        'civilid:delete',
      ],
    },
    {
      title: 'Department Management',
      key: 'department',
      permissions: [
        'departments:read',
        'departments:create',
        'departments:update',
        'departments:delete',
      ],
    },
  ];

  return (
    <div className="permission-management">
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Permission Management</span>
            <Input
              placeholder="Search permissions..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
          </div>
        }
      >
        <div style={{ marginBottom: 24 }}>
          <Title level={5} style={{ marginBottom: 16 }}>Permission Categories</Title>
          <Row gutter={[16, 16]}>
            {permissionCategories.map(category => (
              <Col xs={24} sm={12} md={8} lg={6} key={category.key}>
                <Card 
                  title={category.title}
                  size="small"
                  headStyle={{ backgroundColor: '#fafafa' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {category.permissions.map(perm => {
                      const permission = permissions.find(p => p.name === perm);
                      return (
                        <div key={perm} style={{ marginBottom: 8 }}>
                          <Text strong style={{ display: 'block' }}>{perm}</Text>
                          {permission?.description && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {permission.description}
                            </Text>
                          )}
                        </div>
                      );
                    })}
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        <Divider>All Permissions</Divider>
        
        <Table 
          columns={columns} 
          dataSource={filteredPermissions} 
          rowKey="name"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );
};

export default PermissionManagement;
